/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import Loading from '../../../components/Loading';
import EmployeeNameSearch from '../../../components/EmployeeNameSearch';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../../styles/layout/ActionBar';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { Table } from '../../../styles/table/Table';
import { EmptyState } from '../../../styles/feedback/EmptyState';
import { ClickableRow, CellLink } from '../../../styles/ui/Link';
import PaginationBar from '../../../components/PaginationBar/PaginationBar';
import SectionMeta, {
   SectionHeaderRow,
} from '../../../components/SectionMeta';

import ItemStatusBadge from './components/ItemStatusBadge';
import PeriodBadge from './components/PeriodBadge';

import { searchScheduleItems } from '../../../services/routing/routingApi';
import { getClusterOrigins } from '../../../services/routing/routingScheduleApi';
import { getDepartments } from '../../../services/hr/hrApi';
import axios from '../../../services/axios';
import { notifyApiError } from '../../../utils/feedback';

// =============================================================================
// Configuração e helpers locais
// =============================================================================

const ITEMS_PER_PAGE = 20;

// extraParams para o EmployeeNameSearch — restringe busca a técnicos não-desligados.
// Vai como spread em axios params, então a chave precisa ser snake_case
// (o backend lê req.query.is_technician e req.query.employment_status_not).
// employment_status_not aceita CSV; usamos só 'DESLIGADO' para incluir
// ativos, em experiência, afastados e em férias.
const TECHNICIAN_SEARCH_PARAMS = {
   is_technician: 1,
   employment_status_not: 'DESLIGADO',
};

// Whitelist de colunas ordenáveis — espelha a whitelist do backend.
// Qualquer valor fora disso é silenciosamente substituído por "schedule_date"
// no backend, então mantemos a UI alinhada para evitar surpresas.
const SORTABLE_COLUMNS = new Set([
   'schedule_date',
   'schedule_period',
   'order_number',
   'customer_name',
   'order_value',
   'item_status',
   'created_at',
]);

// Whitelist de status — espelha o ENUM do backend (Fase 1 backend, 2026-05-05).
// Default invisível = backend exclui CANCELADO quando o filtro vai vazio.
const STATUS_OPTIONS = [
   { value: 'PENDENTE',      label: 'Pendente' },
   { value: 'REALIZADO',     label: 'Realizado' },
   { value: 'NAO_REALIZADO', label: 'Não realizado' },
   { value: 'CANCELADO',     label: 'Cancelado' },
];

// Parse do item_status da URL: aceita CSV ("PENDENTE,REALIZADO"). Filtra valores
// fora da whitelist pra evitar passar lixo pro backend (que rejeita 422).
function parseStatusFromUrl(value) {
   if (!value) return [];
   const valid = new Set(STATUS_OPTIONS.map((opt) => opt.value));
   return String(value)
      .split(',')
      .map((part) => part.trim().toUpperCase())
      .filter((part) => valid.has(part));
}

// Filtro de comissão (handoff 0140). O valor do select é a própria string que
// vai na URL (has_commission=true|false); vazio = todos (campo omitido).
const COMMISSION_FILTER_OPTIONS = [
   { value: '', label: 'Todos' },
   { value: 'true', label: 'Com serviço comissionado' },
   { value: 'false', label: 'Sem serviço comissionado' },
];

function parseHasCommissionFromUrl(value) {
   const normalized = String(value ?? '').toLowerCase();
   return normalized === 'true' || normalized === 'false' ? normalized : '';
}

// Estado "não ordenado" => backend aplica seus próprios defaults
// (schedule_date / DESC). Não precisamos mandar nada quando key=null.
function normalizeSortKeyFromUrl(value) {
   if (!value) return null;
   return SORTABLE_COLUMNS.has(value) ? value : null;
}

function normalizeSortDirectionFromUrl(value, hasKey) {
   if (!hasKey) return null;
   const upper = String(value ?? '').toUpperCase();
   return upper === 'ASC' ? 'ASC' : 'DESC';
}

// Padrão BillingController: 3 estados por coluna.
//   inativa → ⇅ (sinaliza que a coluna É ordenável)
//   ativa ASC → ▲
//   ativa DESC → ▼
function getSortIcon(activeKey, activeDirection, columnKey) {
   if (activeKey !== columnKey) return ' ⇅';
   return activeDirection === 'ASC' ? ' ▲' : ' ▼';
}

// Default de filtros — últimos 7 dias (hoje incluso). Datas no formato
// ISO YYYY-MM-DD que o backend já valida (Fase 0 entregue).
function getDefaultDateFrom() {
   const date = new Date();
   date.setDate(date.getDate() - 6); // 7 dias incluindo hoje (hoje + 6 anteriores)
   return date.toISOString().slice(0, 10);
}

function getDefaultDateTo() {
   return new Date().toISOString().slice(0, 10);
}

function formatDateBr(isoDate) {
   if (!isoDate) return '';
   const [year, month, day] = String(isoDate).slice(0, 10).split('-');
   return `${day}/${month}/${year}`;
}

// Formatter de moeda BRL com tratamento robusto: aceita number ou string
// (decimal vira number via Number()) e devolve "—" pra null/NaN. O backend
// pode mandar order_value como string "1234.56" — Number() trata.
function formatCurrencyBr(value) {
   if (value == null || value === '') return '—';
   const num = typeof value === 'number' ? value : Number(value);
   if (Number.isNaN(num)) return '—';
   return num.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

// Render do nome do técnico na coluna — cobre os 3 casos do backend:
//   1. técnico atribuído (assignedTechnician.full_name)
//   2. freelance sem técnico FK (is_freelance_technician=1)
//   3. sem atribuição (PENDENTE típico)
function renderTechnicianCell(item) {
   if (item.assignedTechnician?.full_name) {
      return item.assignedTechnician.full_name;
   }
   if (item.is_freelance_technician) {
      return <span style={{ color: '#9ca3af' }}>— freelance</span>;
   }
   return <span style={{ color: '#9ca3af' }}>—</span>;
}

// Coluna de comissão (handoff 0140). "n/a" se decide pela CONTAGEM de
// lançamentos, nunca por total === 0: lançamento zerado mostra R$ 0,00.
// commission === null = sem permissão de leitura — a coluna nem renderiza
// nesse caso; o "—" é só defesa caso a permissão da sessão esteja defasada.
function renderCommissionCell(commission) {
   if (!commission) return <span style={{ color: '#9ca3af' }}>—</span>;
   if (!commission.entries_count) {
      return <span style={{ color: '#9ca3af' }}>n/a</span>;
   }
   return formatCurrencyBr(commission.total);
}

// =============================================================================
// Componente principal
// =============================================================================

export default function RoutingItemList() {
   const navigate = useNavigate();

   const permissions = useSelector(
      (state) => state.auth.user?.permissions || []
   );
   const canRead = permissions.includes('routing:schedule_item:read');
   // Ler comissão (coluna + filtro has_commission) segue o perfil do GET
   // /routing/items/:id/commission-entries — mesma regra da ficha. Sem ela o
   // backend responde 403 ao filtro em vez de ignorá-lo, então o campo nunca
   // sai deste front para quem não tem a permissão (handoff 0140 §4).
   const canSeeCommission =
      permissions.includes('routing:commission_entry:create') ||
      permissions.includes('routing:finance:operate');

   // ─── Query params da URL ────────────────────────────────────
   // A URL é a source of truth do filtro: lemos dela no mount pra restaurar
   // estado (deep-link, F5, voltar-do-detalhe) e escrevemos nela no submit/
   // sort/paginação. Alterações posteriores na URL durante a sessão NÃO
   // sobrescrevem os filtros do usuário — initialização condicional via
   // getter do useState garante leitura única no mount.
   const [searchParams, setSearchParams] = useSearchParams();
   const hasInitialQueryParams = searchParams.toString() !== '';

   // ─── Estado: dados ──────────────────────────────────────────
   const [items, setItems] = useState([]);
   const [totalCount, setTotalCount] = useState(0);
   // Valor total agregado do backend — soma dos order_value de TODOS os
   // items que casam com o filtro (não só os da página atual).
   const [totalValue, setTotalValue] = useState(0);
   const [totalPages, setTotalPages] = useState(1);
   const [currentPage, setCurrentPage] = useState(() => {
      const fromUrl = Number(searchParams.get('page'));
      return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1;
   });
   const [isLoading, setIsLoading] = useState(false);

   // ─── Estado: catálogo de departamentos (1 fetch no mount) ───
   const [departments, setDepartments] = useState([]);
   const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);

   // ─── Estado: bases operacionais (Ponto 4 — filtro opcional) ──
   const [clusterOrigins, setClusterOrigins] = useState([]);

   // ─── Estado: filtros ────────────────────────────────────────
   // Inicialização condicional: se houver query params na URL, prevalecem;
   // caso contrário, usa os defaults (últimos 7 dias).
   const [dateFrom, setDateFrom] = useState(
      () => searchParams.get('date_from') ?? getDefaultDateFrom()
   );
   const [dateTo, setDateTo] = useState(
      () => searchParams.get('date_to') ?? getDefaultDateTo()
   );
   const [schedulePeriod, setSchedulePeriod] = useState(
      () => searchParams.get('schedule_period') ?? ''
   );
   const [itemStatuses, setItemStatuses] = useState(
      () => parseStatusFromUrl(searchParams.get('item_status'))
   );
   const [departmentId, setDepartmentId] = useState(
      () => searchParams.get('department_id') ?? ''
   );
   const [clusterOrigin, setClusterOrigin] = useState(
      () => (searchParams.get('cluster_origin') ?? '').toUpperCase()
   );
   const [searchTerm, setSearchTerm] = useState(
      () => searchParams.get('search_term') ?? ''
   );
   // Guard de deep-link: sem permissão, has_commission da URL (ex.: link
   // colado por alguém da logística) é descartado — senão a busca tomaria 403
   // e o interceptor mandaria a tela inteira para /403.
   const [hasCommission, setHasCommission] = useState(() =>
      canSeeCommission
         ? parseHasCommissionFromUrl(searchParams.get('has_commission'))
         : ''
   );

   // ─── Estado: ordenação ──────────────────────────────────────
   // Padrão BillingController: 3 estados por coluna (none → ASC → DESC → none).
   // Quando sortKey = null, o backend aplica seus próprios defaults
   // (schedule_date / DESC), então NÃO mandamos sort_by/sort_direction no body.
   // Deep-linkable via ?sort_by= e ?sort_direction= (opcional).
   const [sortKey, setSortKey] = useState(
      () => normalizeSortKeyFromUrl(searchParams.get('sort_by'))
   );
   const [sortDirection, setSortDirection] = useState(
      () => normalizeSortDirectionFromUrl(
         searchParams.get('sort_direction'),
         Boolean(normalizeSortKeyFromUrl(searchParams.get('sort_by')))
      )
   );

   // EmployeeNameSearch é controlado pelo pai com 2 estados:
   //   technicianSearchText → string exibida no input
   //   selectedTechnician   → objeto com .id (vai pra query como technician_id)
   const [technicianSearchText, setTechnicianSearchText] = useState('');
   const [selectedTechnician, setSelectedTechnician] = useState(null);

   // Quando a URL traz technician_id mas não o nome, precisamos buscar o
   // funcionário antes de montar o EmployeeNameSearch — caso contrário ele
   // monta sem initialSelected e dispara busca espúria assim que setamos o
   // texto. isTechnicianHydrating gateia o render do autocomplete durante
   // essa janela.
   const [isTechnicianHydrating, setIsTechnicianHydrating] = useState(
      () => Boolean(searchParams.get('technician_id'))
   );

   // ─── Carregamento inicial: catálogo de departamentos ─────────
   useEffect(() => {
      let cancelled = false;
      setIsLoadingDepartments(true);

      getDepartments()
         .then((list) => {
            if (cancelled) return;
            setDepartments(Array.isArray(list) ? list : []);
         })
         .catch(() => {
            if (cancelled) return;
            // Falha silenciosa — usuário ainda pode filtrar pelo resto.
            // Se quiser ruidoso: toast.warn('Não foi possível carregar departamentos.');
            setDepartments([]);
         })
         .finally(() => {
            if (!cancelled) setIsLoadingDepartments(false);
         });

      return () => {
         cancelled = true;
      };
   }, []);

   // ─── Carregamento inicial: bases operacionais (filtro opcional) ──────
   useEffect(() => {
      let cancelled = false;
      getClusterOrigins()
         .then((list) => {
            if (!cancelled) setClusterOrigins(Array.isArray(list) ? list : []);
         })
         .catch(() => {
            // Falha silenciosa — o filtro de base some, o resto da busca segue.
            if (!cancelled) setClusterOrigins([]);
         });
      return () => {
         cancelled = true;
      };
   }, []);

   // ─── Auto-fetch quando URL trouxer query params (deep-link) ─────────
   // Roda uma única vez no mount. Se o usuário entra na tela via redirect
   // (ex: pós-importação) ou volta do detalhe com filtros na URL, dispara
   // a busca sem exigir clique no "Pesquisar". Se a URL estiver limpa, NÃO
   // faz nada — preserva o padrão "busca só no submit" da seção 6.4 do
   // handoff.
   //
   // Quando a URL traz technician_id, hidratamos o funcionário via GET
   // /hr/employees/:id ANTES de buscar — assim o filtro de técnico aparece
   // no input já com o nome do servidor (URL guarda só o id) e o fetch
   // recebe o id via override (evita race com setState).
   useEffect(() => {
      if (!hasInitialQueryParams) return;

      const techIdFromUrl = searchParams.get('technician_id');
      const initialPage = Number(searchParams.get('page')) || 1;

      if (!techIdFromUrl) {
         // eslint-disable-next-line no-use-before-define
         fetchItems(initialPage);
         return;
      }

      axios
         .get(`/hr/employees/${techIdFromUrl}`)
         .then((res) => {
            const employee = res.data?.data?.employee;
            if (employee) {
               setSelectedTechnician(employee);
               setTechnicianSearchText(employee.full_name ?? '');
            }
         })
         .catch(() => {
            // Falha silenciosa — segue a busca com o id da URL mesmo sem
            // popular o input. O backend ainda filtra corretamente.
         })
         .finally(() => {
            setIsTechnicianHydrating(false);
            // eslint-disable-next-line no-use-before-define
            fetchItems(initialPage, undefined, techIdFromUrl);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ─── Serialização do estado de busca pra URL ────────────────
   // Reflete os filtros + sort + página na URL via setSearchParams.
   // Convenção:
   //   - Datas sempre vão (têm default explícito de 7 dias)
   //   - Demais filtros omitem se vazio (URL mais curta no caso default)
   //   - page só vai se > 1
   //   - sort_by/sort_direction só vão se sortKey != null
   // Sempre usa replace: true — evita poluir o histórico do browser e
   // mantém o back natural (lista → detalhe → back volta pra lista cheia).
   const commitFiltersToUrl = ({ page, sortKey: sk, sortDirection: sd }) => {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (schedulePeriod) params.schedule_period = schedulePeriod;
      if (itemStatuses.length > 0) params.item_status = itemStatuses.join(',');
      if (selectedTechnician?.id) {
         params.technician_id = String(selectedTechnician.id);
      }
      if (departmentId) params.department_id = String(departmentId);
      if (clusterOrigin) params.cluster_origin = clusterOrigin;
      if (searchTerm.trim()) params.search_term = searchTerm.trim();
      if (canSeeCommission && hasCommission) {
         params.has_commission = hasCommission;
      }
      if (page > 1) params.page = String(page);
      if (sk) {
         params.sort_by = sk;
         params.sort_direction = sd;
      }
      setSearchParams(params, { replace: true });
   };

   // ─── Busca paginada de items ────────────────────────────────
   // sortOverride permite que handlers (handleSort) forcem o sort sem esperar
   // o re-render do setState — evita disparar uma busca com o sort antigo.
   // Use { sortKey: null, sortDirection: null } pra explicitamente voltar ao
   // sort default do backend.
   // technicianIdOverride: mesmo padrão pro id do técnico — usado pela
   // hidratação inicial (URL→fetch acontece antes do setSelectedTechnician
   // commitar no estado).
   const fetchItems = async (
      page = 1,
      sortOverride = undefined,
      technicianIdOverride = undefined
   ) => {
      setIsLoading(true);

      const effectiveSortKey =
         sortOverride === undefined ? sortKey : sortOverride.sortKey;
      const effectiveSortDirection =
         sortOverride === undefined ? sortDirection : sortOverride.sortDirection;
      const effectiveTechnicianId =
         technicianIdOverride !== undefined
            ? technicianIdOverride
            : selectedTechnician?.id;

      const filters = {
         dateFrom: dateFrom || undefined,
         dateTo: dateTo || undefined,
         schedulePeriod: schedulePeriod || undefined,
         // Backend aceita string ou array. Vazio → omite, e o default exclui
         // CANCELADO (por isso o usuário precisa marcar o checkbox pra ver).
         itemStatus: itemStatuses.length > 0 ? itemStatuses : undefined,
         technicianId: effectiveTechnicianId || undefined,
         departmentId: departmentId || undefined,
         // Base opcional (Ponto 4) — vazio → todas as bases.
         clusterOrigin: clusterOrigin || undefined,
         searchTerm: searchTerm.trim() || undefined,
         // Vazio → omitido (todos). Nunca vai sem a permissão (403 no backend).
         hasCommission:
            canSeeCommission && hasCommission
               ? hasCommission === 'true'
               : undefined,
         page,
         limit: ITEMS_PER_PAGE,
         sortBy: effectiveSortKey || undefined,
         sortDirection: effectiveSortKey ? effectiveSortDirection : undefined,
      };

      try {
         const result = await searchScheduleItems(filters);
         setItems(result.scheduleItems);
         setTotalCount(result.total);
         setTotalValue(result.totalValue ?? 0);
         setTotalPages(result.totalPages);
         setCurrentPage(page);
      } catch (err) {
         // FC4: o interceptor de contrato já entrega ApiError; a sessão morta
         // (401) é tratada pelo saga, que redireciona para o login.
         notifyApiError(err, {
            fallback: 'Erro ao buscar itens de roteirização.',
            onRetry: () =>
               fetchItems(page, sortOverride, technicianIdOverride),
         });
      } finally {
         setIsLoading(false);
      }
   };

   // ─── Handlers ───────────────────────────────────────────────
   const handleSearch = (e) => {
      e.preventDefault();

      // Validação de range (UX — backend também valida 422):
      if (dateFrom && dateTo && dateFrom > dateTo) {
         toast.error('A data inicial não pode ser maior que a data final.');
         return;
      }
      if ((dateFrom && !dateTo) || (!dateFrom && dateTo)) {
         toast.error('Informe data inicial e final juntas, ou deixe ambas em branco.');
         return;
      }

      commitFiltersToUrl({ page: 1, sortKey, sortDirection });
      fetchItems(1);
   };

   const handleClearFilters = () => {
      setDateFrom(getDefaultDateFrom());
      setDateTo(getDefaultDateTo());
      setSchedulePeriod('');
      setItemStatuses([]);
      setDepartmentId('');
      setClusterOrigin('');
      setSearchTerm('');
      setHasCommission('');
      setTechnicianSearchText('');
      setSelectedTechnician(null);
      setSortKey(null);
      setSortDirection(null);
      setItems([]);
      setTotalCount(0);
      setTotalValue(0);
      setTotalPages(1);
      setCurrentPage(1);
      setSearchParams({}, { replace: true });
   };

   // Toggle de um status no checkbox group (sem submit automático — usuário
   // ainda precisa clicar Pesquisar, consistente com os demais filtros).
   const handleToggleStatus = (statusValue) => {
      setItemStatuses((prev) =>
         prev.includes(statusValue)
            ? prev.filter((s) => s !== statusValue)
            : [...prev, statusValue]
      );
   };

   // Click no header (padrão BillingController): cicla por 3 estados —
   //   inativa  →  ASC
   //   ASC      →  DESC
   //   DESC     →  inativa (volta ao sort default do backend)
   // Sempre volta pra página 1 para evitar página vazia.
   const handleSort = (columnKey) => {
      if (!SORTABLE_COLUMNS.has(columnKey)) return;

      let nextKey;
      let nextDirection;
      if (sortKey !== columnKey) {
         nextKey = columnKey;
         nextDirection = 'ASC';
      } else if (sortDirection === 'ASC') {
         nextKey = columnKey;
         nextDirection = 'DESC';
      } else {
         nextKey = null;
         nextDirection = null;
      }

      setSortKey(nextKey);
      setSortDirection(nextDirection);
      commitFiltersToUrl({
         page: 1,
         sortKey: nextKey,
         sortDirection: nextDirection,
      });
      fetchItems(1, { sortKey: nextKey, sortDirection: nextDirection });
   };

   const handlePageChange = (page) => {
      commitFiltersToUrl({ page, sortKey, sortDirection });
      fetchItems(page);
   };

   const handleRowDoubleClick = (itemId) => {
      navigate(`/routing/items/${itemId}`);
   };

   // ─── Memo: rótulo do range pra subtítulo ────────────────────
   const dateRangeLabel = useMemo(() => {
      if (!dateFrom && !dateTo) return 'sem filtro de data';
      if (dateFrom === dateTo) return `dia ${formatDateBr(dateFrom)}`;
      return `de ${formatDateBr(dateFrom)} até ${formatDateBr(dateTo)}`;
   }, [dateFrom, dateTo]);

   // ─── Guard de permissão (defesa em profundidade — PrivateRoute já cuida) ─
   if (!canRead) {
      return (
         <Container>
            <Section>
               <EmptyState
                  title="Sem permissão"
                  description="Você não tem acesso ao módulo de Roteirização."
               />
            </Section>
         </Container>
      );
   }

   // ─── Render ──────────────────────────────────────────────────
   return (
      <Container>
         <Loading isLoading={isLoading || isLoadingDepartments} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Roteirização — Serviços</PageTitle>
               <PageSubtitle>
                  Consulte serviços agendados. Duplo clique na linha para abrir o
                  detalhe.
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         {/* ─── Filtros ──────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Pesquisa</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid $cols={12} $colsMobile={12}>
                     <Field $col={3} $colMobile={6}>
                        <label>Data inicial</label>
                        <input
                           type="date"
                           value={dateFrom}
                           onChange={(e) => setDateFrom(e.target.value)}
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Data final</label>
                        <input
                           type="date"
                           value={dateTo}
                           onChange={(e) => setDateTo(e.target.value)}
                        />
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Período</label>
                        <select
                           value={schedulePeriod}
                           onChange={(e) => setSchedulePeriod(e.target.value)}
                        >
                           <option value="">Todos</option>
                           <option value="MANHA">Manhã</option>
                           <option value="TARDE">Tarde</option>
                        </select>
                     </Field>

                     {canSeeCommission && (
                        <Field $col={3} $colMobile={6}>
                           <label>Comissão</label>
                           <select
                              value={hasCommission}
                              onChange={(e) => setHasCommission(e.target.value)}
                           >
                              {COMMISSION_FILTER_OPTIONS.map((opt) => (
                                 <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                 </option>
                              ))}
                           </select>
                        </Field>
                     )}

                     <Field $col={12} $colMobile={12}>
                        <label>Status</label>
                        <div
                           role="group"
                           aria-label="Filtro por status"
                           style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '14px',
                              padding: '6px 2px',
                           }}
                        >
                           {STATUS_OPTIONS.map((opt) => (
                              <label
                                 key={opt.value}
                                 style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: '0.85rem',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                 }}
                              >
                                 <input
                                    type="checkbox"
                                    checked={itemStatuses.includes(opt.value)}
                                    onChange={() => handleToggleStatus(opt.value)}
                                    disabled={isLoading}
                                 />
                                 {opt.label}
                              </label>
                           ))}
                           {itemStatuses.length === 0 && (
                              <span
                                 style={{
                                    fontSize: '0.7rem',
                                    color: '#888',
                                    alignSelf: 'center',
                                 }}
                              >
                                 (sem seleção: traz tudo, exceto cancelados)
                              </span>
                           )}
                        </div>
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label>Técnico</label>
                        {isTechnicianHydrating ? (
                           <input
                              type="text"
                              value=""
                              disabled
                              placeholder="Carregando técnico..."
                           />
                        ) : (
                           <EmployeeNameSearch
                              value={technicianSearchText}
                              onChange={setTechnicianSearchText}
                              onSelect={(employee) => {
                                 setSelectedTechnician(employee);
                                 setTechnicianSearchText(employee?.full_name ?? '');
                              }}
                              initialSelected={selectedTechnician}
                              extraParams={TECHNICIAN_SEARCH_PARAMS}
                              placeholder="Digite o nome do técnico..."
                           />
                        )}
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Departamento</label>
                        <select
                           value={departmentId}
                           onChange={(e) => setDepartmentId(e.target.value)}
                           disabled={isLoadingDepartments}
                        >
                           <option value="">Todos</option>
                           {departments.map((dept) => (
                              <option key={dept.id} value={dept.id}>
                                 {dept.department_name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={3} $colMobile={6}>
                        <label>Base</label>
                        <select
                           value={clusterOrigin}
                           onChange={(e) =>
                              setClusterOrigin(e.target.value.toUpperCase())
                           }
                        >
                           <option value="">Todas</option>
                           {clusterOrigins.map((origin) => (
                              <option
                                 key={origin.origin_code}
                                 value={origin.origin_code}
                              >
                                 {origin.origin_label || origin.origin_code}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={5} $colMobile={12}>
                        <label>Pedido / Cliente</label>
                        <input
                           type="text"
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                           placeholder="Buscar por nº pedido ou nome do cliente..."
                        />
                     </Field>

                     <Field $col={12} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <ActionBar>
                           <ActionBarRight>
                              <button
                                 type="button"
                                 onClick={handleClearFilters}
                                 disabled={isLoading}
                              >
                                 Limpar
                              </button>
                              <button type="submit" disabled={isLoading}>
                                 Pesquisar
                              </button>
                           </ActionBarRight>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </form>
            </SectionBody>
         </Section>

         {/* ─── Resultados ───────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionHeaderRow>
                  <SectionTitle>Resultados ({dateRangeLabel})</SectionTitle>
                  <SectionMeta
                     items={[
                        { key: 'count', label: 'Total:', value: totalCount },
                        {
                           key: 'total_value',
                           label: 'Valor total:',
                           value: formatCurrencyBr(totalValue),
                        },
                        {
                           key: 'page',
                           label: 'Página',
                           value: `${currentPage}/${totalPages || 1}`,
                        },
                        {
                           key: 'items_per_page',
                           label: 'Itens por página',
                           value: `${ITEMS_PER_PAGE}`,
                        },
                     ]}
                  />
               </SectionHeaderRow>
            </SectionHeader>

            <SectionBody>
               {items.length ? (
                  <>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th
                                    style={{
                                       textAlign: 'center',
                                       width: 110,
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    onClick={() => handleSort('schedule_date')}
                                    title="Ordenar por data"
                                 >
                                    Data
                                    {getSortIcon(
                                       sortKey,
                                       sortDirection,
                                       'schedule_date'
                                    )}
                                 </th>
                                 <th
                                    style={{
                                       textAlign: 'center',
                                       width: 80,
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    onClick={() => handleSort('schedule_period')}
                                    title="Ordenar por período"
                                 >
                                    Período
                                    {getSortIcon(
                                       sortKey,
                                       sortDirection,
                                       'schedule_period'
                                    )}
                                 </th>
                                 <th
                                    style={{
                                       textAlign: 'left',
                                       width: 100,
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    onClick={() => handleSort('order_number')}
                                    title="Ordenar por nº do pedido"
                                 >
                                    Pedido
                                    {getSortIcon(
                                       sortKey,
                                       sortDirection,
                                       'order_number'
                                    )}
                                 </th>
                                 <th
                                    style={{
                                       textAlign: 'left',
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    onClick={() => handleSort('customer_name')}
                                    title="Ordenar por cliente"
                                 >
                                    Cliente
                                    {getSortIcon(
                                       sortKey,
                                       sortDirection,
                                       'customer_name'
                                    )}
                                 </th>
                                 <th
                                    style={{
                                       textAlign: 'right',
                                       width: 120,
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    onClick={() => handleSort('order_value')}
                                    title="Ordenar por valor"
                                 >
                                    Valor
                                    {getSortIcon(
                                       sortKey,
                                       sortDirection,
                                       'order_value'
                                    )}
                                 </th>
                                 {/* Sem ordenação: commission não está na
                                     whitelist de sort_by (navegacao.md 1.10) */}
                                 {canSeeCommission && (
                                    <th style={{ textAlign: 'right', width: 120 }}>
                                       Comissão
                                    </th>
                                 )}
                                 <th
                                    style={{
                                       textAlign: 'center',
                                       width: 130,
                                       cursor: 'pointer',
                                       userSelect: 'none',
                                    }}
                                    onClick={() => handleSort('item_status')}
                                    title="Ordenar por status"
                                 >
                                    Status
                                    {getSortIcon(
                                       sortKey,
                                       sortDirection,
                                       'item_status'
                                    )}
                                 </th>
                                 <th style={{ textAlign: 'left', width: 180 }}>
                                    Técnico
                                 </th>
                              </tr>
                           </thead>
                           <tbody>
                              {items.map((item) => {
                                 const isItemCancelled =
                                    item.item_status === 'CANCELADO';
                                 // Linha cancelada: opacity dimme tudo, e o
                                 // customer_name ganha strikethrough pra
                                 // distinção visual rápida na varredura.
                                 const rowStyle = isItemCancelled
                                    ? { opacity: 0.55 }
                                    : undefined;
                                 const customerCellStyle = isItemCancelled
                                    ? { textDecoration: 'line-through' }
                                    : undefined;

                                 return (
                                    <ClickableRow
                                       key={String(item.id)}
                                       onDoubleClick={() =>
                                          handleRowDoubleClick(item.id)
                                       }
                                       style={rowStyle}
                                       title={
                                          isItemCancelled
                                             ? 'Item cancelado — duplo clique abre o detalhe'
                                             : 'Duplo clique para abrir o item'
                                       }
                                    >
                                       <td style={{ textAlign: 'center' }}>
                                          {formatDateBr(item.schedule_date)}
                                       </td>
                                       <td style={{ textAlign: 'center' }}>
                                          <PeriodBadge
                                             value={item.schedule_period}
                                          />
                                       </td>
                                       <td>
                                          {/* cor de link do DS; a navegação é
                                              o duplo clique da linha */}
                                          <CellLink as="span">
                                             {item.order_number ?? '—'}
                                          </CellLink>
                                       </td>
                                       <td style={customerCellStyle}>
                                          {item.customer_name ?? '—'}
                                       </td>
                                       <td
                                          style={{
                                             textAlign: 'right',
                                             fontVariantNumeric: 'tabular-nums',
                                          }}
                                       >
                                          {formatCurrencyBr(item.order_value)}
                                       </td>
                                       {canSeeCommission && (
                                          <td
                                             style={{
                                                textAlign: 'right',
                                                fontVariantNumeric:
                                                   'tabular-nums',
                                             }}
                                          >
                                             {renderCommissionCell(
                                                item.commission
                                             )}
                                          </td>
                                       )}
                                       <td style={{ textAlign: 'center' }}>
                                          <ItemStatusBadge
                                             value={item.item_status}
                                          />
                                       </td>
                                       <td>{renderTechnicianCell(item)}</td>
                                    </ClickableRow>
                                 );
                              })}
                           </tbody>
                        </Table>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                     />
                  </>
               ) : (
                  <EmptyState
                     title="Nenhum item encontrado"
                     description="Ajuste os filtros e clique em Pesquisar."
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
